import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { apiMultipart } from '../utils/api';

const AddMaterialModal = ({ projectId, open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    specifications: '',
    brand: '',
    model: '',
    estimated_cost: ''
  });
  const [photos, setPhotos] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handlePhotoChange = (e) => {
    setPhotos(Array.from(e.target.files));
  };

  const handleBrochureChange = (e) => {
    setBrochures(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('project_id', projectId);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('specifications', formData.specifications);
      formDataToSend.append('brand', formData.brand);
      formDataToSend.append('model', formData.model);
      formDataToSend.append('estimated_cost', formData.estimated_cost);

      photos.forEach(photo => {
        formDataToSend.append('photos', photo);
      });

      brochures.forEach(brochure => {
        formDataToSend.append('brochures', brochure);
      });

      await apiMultipart.post('/materials/upload-with-photos', formDataToSend);

      toast.success('Material submitted for admin review');
      setFormData({ category: '', name: '', specifications: '', brand: '', model: '', estimated_cost: '' });
      setPhotos([]);
      setBrochures([]);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add material');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Material for Approval</DialogTitle>
          <DialogDescription>
            Upload material details, photos, and brochures for customer approval
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., Flooring, Sanitary"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Item Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Vitrified Tiles"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="specifications">Specifications *</Label>
            <Textarea
              id="specifications"
              value={formData.specifications}
              onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
              placeholder="Describe the material specifications..."
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="brand">Brand</Label>
              <Input
                id="brand"
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                placeholder="e.g., Kajaria"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Input
                id="model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                placeholder="e.g., Eternity Series"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="estimated_cost">Estimated Cost (₹) *</Label>
            <Input
              id="estimated_cost"
              type="number"
              value={formData.estimated_cost}
              onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
              placeholder="e.g., 125000"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="photos">Product Photos</Label>
            <Input
              id="photos"
              type="file"
              multiple
              accept="image/*"
              onChange={handlePhotoChange}
              className="cursor-pointer"
            />
            {photos.length > 0 && (
              <p className="text-sm text-stone-600">{photos.length} photo(s) selected</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="brochures">Brochures/Documents (PDF)</Label>
            <Input
              id="brochures"
              type="file"
              multiple
              accept=".pdf,.doc,.docx"
              onChange={handleBrochureChange}
              className="cursor-pointer"
            />
            {brochures.length > 0 && (
              <p className="text-sm text-stone-600">{brochures.length} document(s) selected</p>
            )}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3">
            <p className="text-sm text-yellow-900">
              <strong>Note:</strong> This material will be reviewed by admin before being shown to the customer for approval.
            </p>
          </div>

          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-orange-700 hover:bg-orange-800"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Submit Material
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMaterialModal;
